import { Controller } from "@hotwired/stimulus"

// TomSelect emits one change per item, including while it restores the
// server-rendered selection on connect, so company changes are coalesced
// before the options frame is reloaded.
const COMPANY_CHANGE_DEBOUNCE_MS = 250
const RADAR_ALERT_TYPES = [
  "device_offline",
  "new_device",
  "agent_stale",
  "scan_stale",
  "scan_failed",
]

export default class extends Controller {
  static values = {
    assetExpirationTypesUrl: String,
    companyIds: Array,
    allCompanyOptions: Array,
    radarCompanyOptions: Array,
    allCompaniesLabel: String,
    radarCompaniesLabel: String,
  }

  static targets = [
    "alertTypeInputs",
    "webhookInputs",
    "webhookVariables",
    "webhookOptionalLabel",
    "webhookPayloadGroup",
    "expirationInputs",
    "recordChangeInputs",
    "agentStaleInputs",
    "scanStaleInputs",
    "recordTypeSelect",
    "assetLayoutGroup",
    "expirationTypeSelect",
    "assetExpirationTypeGroup",
    "assetExpirationTypeFrame",
    "expirationWebhookVars",
    "singleExpirationWebhookVars",
    "websiteMonitorWebhookVars",
    "passwordViewWebhookVars",
    "recordChangeWebhookVars",
    "passwordChangeWebhookVars",
    "radarDeviceWebhookVars",
    "radarScanWebhookVars",
    "emailGroup",
    "webhookRequiredField",
    "webhookRequiredLabel",
    "companySelectGroup",
  ]

  connect() {
    this.updateVisibility({ resetCompanySelection: false })
  }

  disconnect() {
    clearTimeout(this.companyChangeTimeout)
  }

  updateAlertTypeInputs() {
    this.updateVisibility({ resetCompanySelection: true })
  }

  companiesChanged(event) {
    // TomSelect's search box sits alongside the real select and emits its own
    // change events on blur.
    if (event.target.tagName !== 'SELECT') return

    const ids = Array.from(event.target.options)
      .filter(option => option.selected)
      .map(option => option.value)
      .filter(value => value !== '')

    clearTimeout(this.companyChangeTimeout)
    this.companyChangeTimeout = setTimeout(() => {
      this.reloadAssetExpirationTypes(ids)
    }, COMPANY_CHANGE_DEBOUNCE_MS)
  }

  // Reloading discards the current selection, so skip it when the companies
  // ended up unchanged (a reorder, or TomSelect replaying its initial items).
  reloadAssetExpirationTypes(companyIds) {
    if (!this.hasAssetExpirationTypeFrameTarget || !this.hasAssetExpirationTypesUrlValue) return
    if (this.sameCompanies(companyIds, this.pendingCompanyIds || this.companyIdsValue)) return

    this.pendingCompanyIds = companyIds

    const url = new URL(this.assetExpirationTypesUrlValue, window.location.origin)
    companyIds.forEach(id => url.searchParams.append('company_ids[]', id))
    this.assetExpirationTypeFrameTarget.src = url.toString()
  }

  // companyIdsValue tracks what the frame is actually showing, so it is only
  // committed once the new options render. Turbo cancels a superseded request
  // without erroring, so the last navigation to finish is the current one.
  assetExpirationTypesLoaded() {
    if (this.pendingCompanyIds) {
      this.companyIdsValue = this.pendingCompanyIds
      this.pendingCompanyIds = null
    }

    this.updateAssetExpirationTypeVisibility()
  }

  // Without this the tracked companies would stay ahead of the rendered ones
  // and reselecting them would be treated as a no-op.
  assetExpirationTypesFailed() {
    this.pendingCompanyIds = null
  }

  sameCompanies(a, b) {
    if (a.length !== b.length) return false

    const known = new Set(b.map(String))
    return a.every(id => known.has(String(id)))
  }

  updateRecordTypeInputs() {
    this.updateAssetLayoutVisibility()
  }

  updateExpirationTypeInputs() {
    this.updateAssetExpirationTypeVisibility()
  }

  updateAssetExpirationTypeVisibility() {
    if (!this.hasAssetExpirationTypeGroupTarget || !this.hasExpirationTypeSelectTarget) return

    const alertType = this.getSelectedAlertType()
    const shouldShow =
      (alertType === 'expiration' || alertType === 'single_expiration') &&
      this.expirationTypeSelectTarget.value === 'asset_field'

    this.assetExpirationTypeGroupTarget.classList.toggle('hidden', !shouldShow)
  }

  updateAssetLayoutVisibility() {
    if (!this.hasAssetLayoutGroupTarget || !this.hasRecordTypeSelectTarget) return

    const shouldShow =
      this.getSelectedAlertType() === 'record_change' &&
      this.recordTypeSelectTarget.value === 'Asset'

    this.assetLayoutGroupTarget.classList.toggle('hidden', !shouldShow)
  }

  updateVisibility({ resetCompanySelection = false } = {}) {
    const selectedAlertType = this.getSelectedAlertType()
    const isRadar = RADAR_ALERT_TYPES.includes(selectedAlertType)
    this.hideAllSections()
    this.hideAllWebhookVariables()

    switch (selectedAlertType) {
      case 'single_expiration':
        this.showTarget('expirationInputs')
        this.showTarget('singleExpirationWebhookVars')
        break
      case 'expiration':
        this.showTarget('expirationInputs')
        this.showTarget('expirationWebhookVars')
        break
      case 'record_change':
        this.showTarget('recordChangeInputs')
        this.showTarget('recordChangeWebhookVars')
        break
      case 'website_monitor':
        this.showTarget('websiteMonitorWebhookVars')
        break
      case 'password_view':
        this.showTarget('passwordViewWebhookVars')
        break
      case 'password_change':
        this.showTarget('passwordChangeWebhookVars')
        break
      case 'agent_stale':
        this.showTarget('agentStaleInputs')
        this.showTarget('radarDeviceWebhookVars')
        break
      case 'scan_stale':
        this.showTarget('scanStaleInputs')
        this.showTarget('radarScanWebhookVars')
        break
      case 'device_offline':
      case 'new_device':
        this.showTarget('radarDeviceWebhookVars')
        break
      case 'scan_failed':
        this.showTarget('radarScanWebhookVars')
        break
    }

    if (selectedAlertType === 'record_change') {
      this.emailGroupTarget.classList.add('hidden')
      this.webhookOptionalLabelTarget.classList.add('hidden')
    } else {
      this.emailGroupTarget.classList.remove('hidden')
      this.webhookOptionalLabelTarget.classList.remove('hidden')
    }

    if (this.hasWebhookPayloadGroupTarget) {
      this.webhookPayloadGroupTarget.classList.toggle('hidden', isRadar)
    }

    this.updateEmailRequirement(isRadar)
    // Company option lists only change when crossing Radar ↔ non-Radar.
    // Resetting on every type change (e.g. agent_stale → scan_stale) would
    // wipe a specific company back to "All" and broaden the alert.
    if (resetCompanySelection && this.previousIsRadar !== undefined && this.previousIsRadar !== isRadar) {
      this.updateCompanyOptions(isRadar, { resetSelection: true })
    }
    this.previousIsRadar = isRadar
    this.syncThresholdSelects(selectedAlertType)
    this.updateWebhookRequirement(selectedAlertType === 'record_change')
    this.updateAssetLayoutVisibility()
    this.updateAssetExpirationTypeVisibility()
  }

  // Agent and scan thresholds share name="alert[threshold_minutes]". Disable
  // the hidden select so only the active type submits, and ensure a valid
  // default is selected when switching between absence types.
  syncThresholdSelects(alertType) {
    const agentSelect = this.thresholdSelectFor(this.hasAgentStaleInputsTarget ? this.agentStaleInputsTarget : null)
    const scanSelect = this.thresholdSelectFor(this.hasScanStaleInputsTarget ? this.scanStaleInputsTarget : null)

    if (alertType === 'agent_stale') {
      this.disableThresholdSelect(scanSelect)
      this.enableThresholdSelect(agentSelect, '60')
    } else if (alertType === 'scan_stale') {
      this.disableThresholdSelect(agentSelect)
      this.enableThresholdSelect(scanSelect, '120')
    } else {
      this.disableThresholdSelect(agentSelect)
      this.disableThresholdSelect(scanSelect)
    }
  }

  thresholdSelectFor(section) {
    return section?.querySelector('select[name="alert[threshold_minutes]"]') || null
  }

  disableThresholdSelect(select) {
    if (!select) return

    select.disabled = true
    select.value = ''
  }

  enableThresholdSelect(select, defaultValue) {
    if (!select) return

    select.disabled = false
    if (!select.value) select.value = String(defaultValue)
  }

  // Radar alerts always require email (interface delivery is email + optional URL).
  updateEmailRequirement(isRadar) {
    const emailInput = this.emailGroupTarget.querySelector('input[type="email"]')
    if (!emailInput) return

    if (isRadar) {
      delete emailInput.dataset.requiredInputUnlessFilledValue
    } else {
      emailInput.dataset.requiredInputUnlessFilledValue = '#alert_webhook_url'
    }
  }

  updateCompanyOptions(isRadar, { resetSelection = false } = {}) {
    if (!this.hasCompanySelectGroupTarget) return
    if (!this.hasAllCompanyOptionsValue || !this.hasRadarCompanyOptionsValue) return

    const selectElement = this.companySelectGroupTarget.querySelector('[data-controller~="select"]')
    if (!selectElement) return

    const selectController = this.application.getControllerForElementAndIdentifier(selectElement, 'select')
    if (!selectController || typeof selectController.replaceOptions !== 'function') return

    const companies = isRadar ? this.radarCompanyOptionsValue : this.allCompanyOptionsValue
    const allLabel = isRadar ? this.radarCompaniesLabelValue : this.allCompaniesLabelValue
    const options = [{ id: '__all__', name: allLabel }, ...companies]

    selectController.replaceOptions(options)
    if (resetSelection && selectController.select && typeof selectController.select.setValue === 'function') {
      // Non-silent so the change bubbles to companiesChanged and dependent
      // frames (asset expiration types) reload for the new "All" scope.
      selectController.select.setValue(['__all__'], false)
    }
  }

  // Record change alerts deliver over the webhook instead of email, so the
  // model requires both webhook fields for that type and only that type.
  updateWebhookRequirement(isRequired) {
    this.webhookRequiredFieldTargets.forEach(field => {
      field.dataset.requiredInputRequiredValue = isRequired
    })

    this.webhookRequiredLabelTargets.forEach(label => {
      label.classList.toggle('required', isRequired)
    })
  }

  getSelectedAlertType() {
    const select = this.element.querySelector('select[name="alert[alert_type]"]')
    return select?.value || null
  }

  hideAllSections() {
    const sections = this.element.querySelectorAll('.alert-type-section')
    sections.forEach(section => {
      section.classList.add('hidden')
    })
  }

  hideAllWebhookVariables() {
    const webhookSections = this.element.querySelectorAll('.webhook-vars-section')
    webhookSections.forEach(section => {
      section.classList.add('hidden')
    })
  }

  showTarget(targetName) {
    const capitalized = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${capitalized}Target`]) {
      this[`${targetName}Target`].classList.remove('hidden')
    }
  }

}
